// Tenantra Inc. - View Report: tenant sign-in (email + password) with forgot password
import { useState } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import { Logo } from '@/components/Logo';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import api from '@/services/api';
import { track } from '@/services/analytics';
import RentReportView from './RentReportView';

const inputCls = 'h-10 bg-white border-stone-300 text-stone-900 focus:border-teal-600 focus:ring-teal-600/20 text-sm';
const btnCls = 'w-full h-11 rounded-md bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium disabled:opacity-50 inline-flex items-center justify-center gap-2 transition-colors';
const errText = (err) => {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map(x => x?.msg).filter(Boolean).join(' ');
  return 'Something went wrong. Please try again.';
};

const ErrorBox = ({ msg }) => msg ? (
  <div className="p-2.5 bg-red-50 border border-red-200 rounded-md flex items-center gap-2 text-red-600 text-sm" data-testid="auth-error"><AlertCircle className="w-4 h-4 flex-shrink-0" />{msg}</div>
) : null;

function SignIn({ onSignedIn, onForgot }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError('');
    try { const r = await api.post('/tenant-portal/auth/login', { email: email.trim(), password }); track('tenant_login', { ok: true }); onSignedIn(r.data.session_id, r.data.must_change_password, password); }
    catch (err) { setError(errText(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="tenant-login-form">
      <div className="space-y-1.5"><Label htmlFor="tl-email" className="text-stone-700 text-sm">Email</Label>
        <Input id="tl-email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="tenant-login-email" /></div>
      <div className="space-y-1.5">
        <div className="flex items-center justify-between"><Label htmlFor="tl-pass" className="text-stone-700 text-sm">Password</Label>
          <button type="button" onClick={() => onForgot(email)} className="text-xs text-teal-700 hover:text-teal-800" data-testid="tenant-forgot-link">Forgot password?</button></div>
        <Input id="tl-pass" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} data-testid="tenant-login-password" />
      </div>
      <ErrorBox msg={error} />
      <button type="submit" disabled={busy} className={btnCls} data-testid="tenant-login-submit">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Sign in</button>
    </form>
  );
}

function Forgot({ initialEmail, onBack }) {
  const [email, setEmail] = useState(initialEmail || '');
  const [sent, setSent] = useState(false);
  const [mockOtp, setMockOtp] = useState('');
  const [code, setCode] = useState('');
  const [pw, setPw] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const send = async (e) => {
    e?.preventDefault(); setBusy(true); setError('');
    try { const r = await api.post('/tenant-portal/auth/forgot-password', { email: email.trim() }); setSent(true); setMockOtp(r.data.mock_otp || ''); }
    catch (err) { setError(errText(err)); } finally { setBusy(false); }
  };
  const reset = async (e) => {
    e.preventDefault(); setBusy(true); setError('');
    try { await api.post('/tenant-portal/auth/reset-password', { email: email.trim(), code, new_password: pw }); toast.success('Password updated. Sign in with your new password.'); onBack(); }
    catch (err) { setError(errText(err)); } finally { setBusy(false); }
  };
  if (!sent) return (
    <form onSubmit={send} className="space-y-4" data-testid="tenant-forgot-form">
      <p className="text-sm text-stone-600">Enter your email and we'll send you a 6-digit code to reset your password.</p>
      <div className="space-y-1.5"><Label htmlFor="tf-email" className="text-stone-700 text-sm">Email</Label>
        <Input id="tf-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="tenant-forgot-email" /></div>
      <ErrorBox msg={error} />
      <button type="submit" disabled={busy} className={btnCls} data-testid="tenant-forgot-send">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Send code</button>
    </form>
  );
  return (
    <form onSubmit={reset} className="space-y-4" data-testid="tenant-reset-form">
      <p className="text-sm text-stone-600">If an account exists for <span className="font-medium text-stone-900">{email}</span>, we sent a 6-digit code.</p>
      {mockOtp && <p className="text-xs text-amber-700" data-testid="otp-preview-code">Preview mode code: <span className="font-mono font-semibold">{mockOtp}</span></p>}
      <div className="space-y-1.5"><Label htmlFor="tr-code" className="text-stone-700 text-sm">Code</Label>
        <Input id="tr-code" inputMode="numeric" autoComplete="one-time-code" required value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} className={`${inputCls} font-mono tracking-[0.4em]`} data-testid="tenant-reset-code" /></div>
      <div className="space-y-1.5"><Label htmlFor="tr-pass" className="text-stone-700 text-sm">New password</Label>
        <Input id="tr-pass" type="password" autoComplete="new-password" required minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} className={inputCls} data-testid="tenant-reset-password" />
        <p className="text-[11px] text-stone-500">At least 8 characters.</p></div>
      <ErrorBox msg={error} />
      <button type="submit" disabled={busy || code.length !== 6 || pw.length < 8} className={btnCls} data-testid="tenant-reset-submit">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Set new password</button>
      <button type="button" onClick={send} disabled={busy} className="w-full text-xs text-stone-500 hover:text-stone-800" data-testid="tenant-reset-resend">Send a new code</button>
    </form>
  );
}

function ChangeTemp({ sessionId, currentPassword, onDone }) {
  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const submit = async (e) => {
    e.preventDefault();
    if (pw !== confirm) { setError('Passwords do not match'); return; }
    setBusy(true); setError('');
    try { await api.post('/tenant-portal/auth/change-password', { session_id: sessionId, current_password: currentPassword, new_password: pw }); toast.success('Password updated'); onDone(); }
    catch (err) { setError(errText(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="tenant-change-temp-form">
      <p className="text-sm text-stone-600">You signed in with a temporary password. Choose your own password to continue.</p>
      <div className="space-y-1.5"><Label htmlFor="tc-pass" className="text-stone-700 text-sm">New password</Label>
        <Input id="tc-pass" type="password" autoComplete="new-password" required minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} className={inputCls} data-testid="tenant-new-password" />
        <p className="text-[11px] text-stone-500">At least 8 characters.</p></div>
      <div className="space-y-1.5"><Label htmlFor="tc-confirm" className="text-stone-700 text-sm">Confirm new password</Label>
        <Input id="tc-confirm" type="password" autoComplete="new-password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inputCls} data-testid="tenant-confirm-password" /></div>
      <ErrorBox msg={error} />
      <button type="submit" disabled={busy || pw.length < 8 || !confirm} className={btnCls} data-testid="tenant-change-temp-submit">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Save and continue</button>
    </form>
  );
}

export default function TenantAccessPage() {
  const [mode, setMode] = useState('login');
  const [forgotEmail, setForgotEmail] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);

  const [pendingChange, setPendingChange] = useState(null);
  const onSignedIn = async (sid, mustChange, typedPassword) => {
    if (mustChange) { setPendingChange({ sid, typedPassword }); setMode('change'); return; }
    setLoading(true);
    try { const r = await api.get(`/tenant-portal/rent-report/${sid}`); setSessionId(sid); setReportData(r.data); }
    catch (err) { toast.error(errText(err)); } finally { setLoading(false); }
  };
  const logout = () => { api.post(`/tenant-portal/auth/logout/${sessionId}`).catch(() => {}); setSessionId(''); setReportData(null); setMode('login'); };

  if (loading) return (
    <div className="min-h-screen bg-[#f3f4f6] flex items-center justify-center"><div className="w-12 h-12 border-4 border-slate-200 border-t-teal-600 rounded-full animate-spin" /></div>
  );
  if (reportData) return <RentReportView data={reportData} sessionId={sessionId} onStartOver={logout} />;

  return (
    <div className="min-h-screen bg-[#f3f4f6] flex flex-col" data-testid="tenant-access-page">
      <header className="h-16 bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto h-full px-4 sm:px-6 flex items-center"><a href="/"><Logo size="md" data-testid="auth-logo" /></a></div>
      </header>
      <main className="flex-1 flex items-start justify-center px-4 sm:px-6 py-12 sm:py-20">
        <div className="w-full max-w-[440px]">
          <div className="bg-white border border-slate-200 border-t-2 border-t-teal-600 rounded-md p-8 sm:p-10 shadow-[0_1px_0_rgba(15,23,42,0.03),0_2px_6px_rgba(15,23,42,0.04),0_24px_48px_-24px_rgba(15,23,42,0.18)]">
            <h2 className="text-[26px] leading-tight font-semibold text-stone-900 tracking-[-0.01em] mb-1" style={{ fontFamily: "'Archivo', sans-serif" }} data-testid="tenant-access-title">
              {mode === 'login' ? 'View report' : mode === 'change' ? 'Set your password' : 'Reset your password'}
            </h2>
            <p className="text-sm text-stone-500 mb-7">{mode === 'login' ? 'Sign in to see your rent report and manage your rent payments.' : mode === 'change' ? 'This only happens once.' : 'We\'ll email you a code.'}</p>
            {mode === 'login' && <SignIn onSignedIn={onSignedIn} onForgot={(e) => { setForgotEmail(e); setMode('forgot'); }} />}
            {mode === 'forgot' && <Forgot initialEmail={forgotEmail} onBack={() => setMode('login')} />}
            {mode === 'change' && pendingChange && <ChangeTemp sessionId={pendingChange.sid} currentPassword={pendingChange.typedPassword} onDone={() => { const sid = pendingChange.sid; setPendingChange(null); onSignedIn(sid, false); }} />}
            {mode !== 'login' && <button onClick={() => setMode('login')} className="mt-5 text-sm text-stone-500 hover:text-stone-800" data-testid="tenant-back-to-login">Back to sign in</button>}
          </div>
        </div>
      </main>
    </div>
  );
}
